import CustomView from "@/domain/entities/customViews/CustomView";
import { useToast } from "@/hooks/utils/useToast";
import {
    customViewToDomainMapper,
    customViewToSDKMapper,
} from "@/mappers/customView/customViewMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const useManageFavoriteCustomViews = () => {
    const { customViewManager } = useSdk();
    const { toast } = useToast();
    const { t } = useTranslation();

    const [customViews, setCustomViews] = useState<CustomView[]>([]);

    const fetchCustomViews = async () => {
        try {
            const viewsResult = await customViewManager.getCustomViews();
            const customViewsDomain = viewsResult.map(customViewToDomainMapper);
            setCustomViews(customViewsDomain);
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.customViews.getCustomViews"),
                description: t("errors.global.description"),
                variant: "error",
            });
        }
    };

    const editCustomViewFavoriteStatus = async (
        view: CustomView,
        isFavorite: boolean,
    ): Promise<boolean> => {
        if (!view.id) {
            throw new Error("View ID is required");
        }

        try {
            view.setIsFavorite(isFavorite);
            const sdkView = customViewToSDKMapper(view);

            await customViewManager.editView(view.id, sdkView);

            return true;
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.customViews.editView"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return false;
        }
    };

    const updateFavoritesViewsOrder = async (views: CustomView[]) => {
        try {
            const sdkViews = views.map(customViewToSDKMapper);
            await customViewManager.setFavoriteViews(
                sdkViews.map((view) => view.id!),
            );

            return true;
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.customViews.editView"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return false;
        }
    };

    useEffect(() => {
        fetchCustomViews();
    }, []);

    return {
        customViews,
        editCustomViewFavoriteStatus,
        updateFavoritesViewsOrder,
    };
};

export default useManageFavoriteCustomViews;
